import { useToastContext } from "@ttc3k/trekker";
import {
  EnumPublicationStatusStatus,
  EnumVersionedEntityReferenceType,
  OperatorByIdDocument,
  OperatorByIdQuery,
  OperatorManyDocument,
  PublicationStatusType,
  useCreatePublicationStatusMutation,
  useOperatorDeleteMutation,
  useOperatorUpdateMutation,
} from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const useBackOffice = (
  operator: OperatorByIdQuery["operatorById"],
  operatorEntityId: string | undefined,
  organizationId: string | undefined,
) => {
  const { toastFactory } = useToastContext();
  const organizationEnrollment = operator.enrollments.find(
    (e) => e?.organization === organizationId,
  );
  const isStatusUnpublished =
    operator.status === PublicationStatusType.Unpublished;

  // The following useStates are so the user doesn't have to wait until the
  // API call succeeds to see the change in the UI
  const [currentEnrollment, setCurrentEnrollment] = useState(
    organizationEnrollment,
  );
  const [isUnpublished, setIsUnpublished] = useState(isStatusUnpublished);

  const { t } = useTranslation();
  const [updateOperator] = useOperatorUpdateMutation({
    refetchQueries: [OperatorByIdDocument, OperatorManyDocument],
  });
  const [createPublicationStatus, { loading: loadingCreatePublicationStatus }] =
    useCreatePublicationStatusMutation({
      refetchQueries: [OperatorByIdDocument, OperatorManyDocument],
    });
  const [deleteOperator, { loading: loadingDeleteOperator }] =
    useOperatorDeleteMutation({
      refetchQueries: [OperatorManyDocument],
    });

  const handleUpdateOrganizationControlled = async (
    organizationControlled: boolean,
  ) => {
    try {
      setCurrentEnrollment((prev) => {
        if (prev) {
          return { ...prev, organizationControlled };
        }
        return prev;
      });
      await updateOperator({
        variables: {
          entityId: operatorEntityId ?? "",
          record: {
            enrollments: operator.enrollments.map((e) => {
              if (e?.organization === organizationId) {
                return {
                  ...e,
                  organizationControlled,
                };
              }
              return e;
            }),
          },
        },
      });
    } catch {
      setCurrentEnrollment(organizationEnrollment);
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    }
  };
  const handleUpdatePublicationStatus = async (
    unPublish: boolean,
    onModalClose: () => void,
  ) => {
    try {
      setIsUnpublished(unPublish);
      await createPublicationStatus({
        variables: {
          record: {
            entity: {
              _entityId: operatorEntityId ?? "",
              _versionId: operator._versionId ?? "",
              type: EnumVersionedEntityReferenceType.Operator,
            },
            organization: organizationId,
            status: unPublish
              ? EnumPublicationStatusStatus.Unpublished
              : EnumPublicationStatusStatus.NeedsApproval,
          },
        },
      }).then(onModalClose);
    } catch {
      setIsUnpublished(isStatusUnpublished);
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    }
  };

  const handleDeleteOperator = async (onModalClose: () => void) => {
    try {
      await deleteOperator({
        variables: {
          entityId: operatorEntityId ?? "",
        },
      }).then(onModalClose);
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    }
  };

  return {
    onUpdateOrganizationControlled: handleUpdateOrganizationControlled,
    onUpdatePublicationStatus: handleUpdatePublicationStatus,
    onDeleteOperator: handleDeleteOperator,
    loadingDeleteOperator,
    currentEnrollment,
    isUnpublished,
    loadingCreatePublicationStatus,
  };
};
